<template>
  <div class="mySession">
    <CreateSession
      v-show="showModal"
      @close="closeCreateSession"
      @refetchSessions="fetchSessions"
      :sessions="sessions"
      :credits="credits"
      :totalCreditsForThisMonth="totalCreditsForThisMonth"
      :maxConcurrentSessions="maxConcurrentSessions"
      :usedCreditsThisMonth="usedCreditsThisMonth"
      :templates="templates"
    />
    <ActiveSession
      @newWindowClick="handleWindowClick"
      newWindowLabel="VNC"
      :sessions="sessions"
      :maxConcurrentSessions="maxConcurrentSessions"
      :hoursForecast="hoursForecast"
      :totalCreditsForThisMonth="totalCreditsForThisMonth"
      :usedCreditsThisMonth="usedCreditsThisMonth"
      :isFetchingSession="isFetchingSession"
      @refetchSessions="fetchSessions"
      @addSession="handleAddSession"
      :processing="processing"
      :isVNCOpening="isVNCOpening"
    />
  </div>
</template>

<script>
// @ is an alias to /src
import ActiveSession from "@/components/ActiveSession.vue";
import CreateSession from "@/components/CreateSessionPopup.vue";
import axios from "@/api/index";
import { openCenteredPopup } from "@/utils/openWindow.js";
import { mapState } from "vuex";
import { getSessions, connectVNC, getTemplates } from "@/api/vm";
import { tokenRequest } from "@/config/authConfig";
import { ONE_MINUTE } from "@/config/time";

export default {
  name: "Session",
  components: { ActiveSession, CreateSession },
  data() {
    return {
      showModal: false,
      interval: null,
      processing: false,
      vmCreationState: "Not started",
      isVNCOpening: false,
      isFetchingSession: true,
      templates: []
    };
  },
  async mounted() {
    // window.addEventListener("beforeunload", this.preventPageLeave);
    await this.getToken();
    this.$store.dispatch("fetchCredits");
    this.$store.dispatch("pollCredits", ONE_MINUTE);
    this.$store.dispatch("pollSessions", ONE_MINUTE);
  },
  beforeDestroy() {
    // window.removeEventListener("beforeunload", this.preventPageLeave);
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.$store.commit("stopCreditPoll");
  },
  watch: {
    vmCreationState(newState, oldState) {
      if (oldState !== "Succeeded" && newState === "Succeeded") {
        this.fetchSessions();
      }
    }
  },
  computed: mapState({
    hoursForecast: state => state.credits.hoursForecast,
    totalCreditsForThisMonth: state => state.credits.totalCreditsForThisMonth,
    usedCreditsThisMonth: state => state.credits.usedCreditsThisMonth,
    maxConcurrentSessions: state => state.maxConcurrentSessions,
    sessions: state => state.sessions,
    credits: state => state.credits
  }),
  methods: {
    async handleAddSession() {
      const response = await getTemplates();
      this.showModal = true;
      this.templates = response.data.data;
    },
    closeCreateSession() {
      this.showModal = false;
      this.$store.dispatch("updateSessions");
    },
    // preventPageLeave(event) {
    //   if (!this.processing) return null;
    //   event.preventDefault();
    //   event.returnValue = "Your VM is still being created.";
    //   return "Your VM is still being created.";
    // },
    updateProcessingStatus(status = false) {
      this.processing = status;
    },
    updateVMCreationState(state = "") {
      this.vmCreationState = state;
    },
    async handleWindowClick(e, sessionId) {
      // this.$router.push("/digitalTwin");
      try {
        if (this.isVNCOpening) {
          return;
        }
        this.isVNCOpening = true;
        await connectVNC(sessionId);
        openCenteredPopup(
          `${window.location.origin}/#/session/${sessionId}/live`
        );
        this.isVNCOpening = false;
      } catch (error) {
        this.isVNCOpening = false;
        let message = "Could not connect to VNC";
        if (error && error.response && error.response.data) {
          message = error.response.data || message;
        }
        this.$notification.error({
          message: "Could not connect to VNC",
          description: message
        });
      }
    },
    async getToken() {
      try {
        const userData = await this.$msal.acquireToken(tokenRequest);
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${userData.accessToken}`;
        await this.fetchSessions();
      } catch (error) {
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to retrieve user details"
        });
      }
    },
    async fetchSessions(options = {}) {
      try {
        if (!options.skipLoader) {
          this.isFetchingSession = true;
        }
        const response = await getSessions();
        const {
          sessions = [],
          hoursForecast = 0,
          totalCreditsForThisMonth = 0,
          usedCreditsThisMonth = 0,
          maxConcurrentSessions = 5
        } = response.data ? response.data : {};
        this.$store.commit("setSessions", sessions);
        this.$store.commit("setCredits", {
          hoursForecast: parseFloat(hoursForecast).toFixed(2),
          totalCreditsForThisMonth,
          usedCreditsThisMonth,
          maxConcurrentSessions
        });
        this.$store.commit("setMaxConcurrentSessions", maxConcurrentSessions);
        this.isFetchingSession = false;
      } catch (error) {
        this.isFetchingSession = false;
        this.$notification.error({
          message: error.message || "Could not get user",
          description: "Unable to get VM sessions"
        });
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.processing) {
      const answer = window.confirm(
        "Do you really want to leave? VM is still being created."
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  }
};
</script>

<style lang="scss" scoped>
.mySession {
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
